import { useEffect, useRef, useId, type ReactNode, type HTMLAttributes } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import styles from './Modal.module.css'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen'

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Whether the modal is open */
  open: boolean
  /** Called when the modal requests to close */
  onClose: () => void
  /** Modal max-width preset */
  size?: ModalSize
  /** Optional title rendered in the header */
  title?: ReactNode
  /** Optional footer content (typically action buttons) */
  footer?: ReactNode
  /** Whether clicking the backdrop closes the modal */
  closeOnBackdrop?: boolean
  /** Whether pressing Escape closes the modal */
  closeOnEscape?: boolean
  /** Whether to show the close (X) button */
  showCloseButton?: boolean
  children: ReactNode
}

export function Modal({
  open,
  onClose,
  size = 'md',
  title,
  footer,
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  children,
  className,
  ...props
}: ModalProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [open])

  useEffect(() => {
    if (!open || !closeOnEscape) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, closeOnEscape, onClose])

  useEffect(() => {
    if (!open) return
    previousFocusRef.current = document.activeElement as HTMLElement
    requestAnimationFrame(() => {
      // Only take focus if nothing inside has it yet. The frame can land after
      // an autofocused field has claimed focus, or after the user has begun
      // typing — yanking it back to the container swallows their keystrokes.
      if (dialogRef.current?.contains(document.activeElement)) return
      dialogRef.current?.focus()
    })
    return () => {
      previousFocusRef.current?.focus()
    }
  }, [open])

  function handleBackdropClick(e: React.MouseEvent) {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose()
    }
  }

  const overlayClasses = [
    styles.overlay,
    size === 'fullscreen' && styles.overlayFullscreen,
  ].filter(Boolean).join(' ')

  const dialogClasses = [
    styles.dialog,
    styles[size],
    className,
  ].filter(Boolean).join(' ')

  if (!open) return null

  return createPortal(
    <div className={overlayClasses} onClick={handleBackdropClick}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={dialogClasses}
        tabIndex={-1}
        {...props}
      >
        {(title || showCloseButton) && (
          <div className={styles.header}>
            {title && (
              <div id={titleId} className={styles.title}>{title}</div>
            )}
            {showCloseButton && (
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
